import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import SeoHead from '../components/SeoHead'
import WaveDivider from '../components/WaveDivider'
import { localizeTree } from '../i18n/localizeTree'

const productField = (product, field, lang) =>
  (lang !== 'da' && product[`${field}_${lang}`]) || product[field]

export default function WebshopPage({ lang = 'da' }) {
  const [products, setProducts] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('Alle')
  const [activeProduct, setActiveProduct] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true)
        const res = await fetch('/_api/public/v2/content/products?limit=50')
        if (res.ok) {
          const data = await res.json()
          if (data.items && data.items.length > 0) {
            setProducts(data.items)
            return
          }
        }
      } catch (e) {
        console.warn('Kunne ikke hente produkter fra CMS:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [])

  const categories = ['Alle', 'Fyldte æsker', 'Mandler & Dåser', 'Marcipan', 'Gavekort']

  const filteredProducts = products.filter((p) => {
    if (selectedCategory === 'Alle') return true
    const cat = p.category?.toLowerCase() || ''
    if (selectedCategory === 'Fyldte æsker') return cat.includes('æske') || cat.includes('fyldt')
    if (selectedCategory === 'Mandler & Dåser') return cat.includes('mandel') || cat.includes('dåse') || cat.includes('nød')
    if (selectedCategory === 'Marcipan') return cat.includes('marcipan')
    if (selectedCategory === 'Gavekort') return cat.includes('gavekort')
    return true
  })

  return localizeTree((
    <>
      <SeoHead pageId="webshop" lang="da" />

      {/* Hero / Intro Banner */}
      <section className="subpage-hero bg-[#F7ECEE] py-12 sm:py-16 border-b border-[#C68D94]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A35D67]">
            Håndlavet chokolade & specialiteter
          </span>
          <h1 className="font-script text-5xl sm:text-6xl text-[#3E3435] font-normal my-3">
            Webshop
          </h1>
          <WaveDivider className="text-[#C68D94]/50 mx-auto mb-6" />
          <div className="mt-4 inline-flex items-center gap-2 bg-white/80 border border-[#C68D94]/30 px-5 py-2.5 rounded-full text-xs font-medium text-[#A35D67] shadow-xs">
            <i className="fa-regular fa-truck-fast text-sm" aria-hidden="true" />
            <span>Jeg sender vare ud onsdag, så hvis du vil have noget med så bestil senest onsdag inden kl. 12:00</span>
          </div>
        </div>
      </section>

      {/* Products Catalog Section */}
      <section className="py-12 sm:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Navigation Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition-all ${
                selectedCategory === cat
                  ? 'bg-[#C68D94] text-white shadow-sm'
                  : 'bg-white text-[#3E3435]/80 hover:bg-[#F7ECEE] hover:text-[#A35D67] border border-[#C68D94]/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#A35D67]" />
          </div>
        )}

        {/* Products Grid */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => {
              const mainImg = product.images?.[0]
              return (
                <div
                  key={product.id || product.slug?._ || product.title}
                  onClick={() => setActiveProduct(product)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setActiveProduct(product)
                    }
                  }}
                  className="bg-white rounded-2xl overflow-hidden border border-[#C68D94]/20 shadow-sm flex flex-col justify-between hover:shadow-md hover:border-[#C68D94]/50 transition-all duration-300 group cursor-pointer"
                >
                  <div className="relative aspect-square overflow-hidden bg-stone-50">
                    {mainImg ? (
                      <img
                        src={imageUrl(mainImg, 'width=500,height=500,fit=cover,quality=80,format=auto')}
                        alt={productField(product, 'title', lang)}
                        className="w-full h-full object-cover transition-transform duration-500 "
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <i className="fa-regular fa-image text-3xl" />
                      </div>
                    )}
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-medium text-sm text-[#3E3435] group-hover:text-[#A35D67] transition-colors line-clamp-2">
                                               {productField(product, 'title', lang)}
                                             </h3>
                                             {productField(product, 'description', lang) && (
                                               <p className="text-xs text-[#3E3435]/70 font-light mt-1.5 line-clamp-2 leading-relaxed">
                                                 {productField(product, 'description', lang)}
                                               </p>
                                             )}
                    </div>

                    <div className="mt-4 pt-3 flex items-center justify-between">
                      <span className="font-semibold text-sm text-[#A35D67]">
                        {product.price_display || `${product.price},00 kr.`}
                      </span>
                      <span
                        className="text-xs font-semibold text-[#C68D94] group-hover:text-[#A35D67] flex items-center justify-center p-1 transition-colors"
                        aria-hidden="true"
                      >
                        <i className="fa-solid fa-chevron-right text-xs" />
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* "Din kage – helt efter ønske" Section (from scrape) */}
      <section className="py-16 sm:py-20 bg-white border-t border-[#C68D94]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A35D67]">
            Specialbestillinger & Fest
          </span>
          <h2 className="font-script text-4xl sm:text-5xl text-[#3E3435] font-normal my-3">
            Din kage – helt efter ønske
          </h2>
          <WaveDivider className="text-[#C68D94]/50 mx-auto mb-6" />
          <div className="text-xs sm:text-sm text-[#3E3435]/85 font-light leading-relaxed space-y-4 max-w-2xl mx-auto text-center">
            <p>
              Hos Fanø Chokolade elsker jeg at skabe kager, der gør særlige øjeblikke endnu mere mindeværdige. Uanset om det er til bryllup, fødselsdag, konfirmation eller en helt anden anledning, laver jeg kager efter dine ønsker – både når det gælder smag, udseende og størrelse.
            </p>
            <p>
              Jeg går op i at kombinere æstetik og kvalitet, så kagen både bliver en fryd for øjet og en oplevelse for ganen. Har du en idé eller et tema, tager jeg gerne udgangspunkt i det og hjælper med at finde den helt rigtige løsning.
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/_assets/bryllupskage-skema-dk.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#A35D67] hover:bg-[#8E4C56] text-white px-7 py-3.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors shadow-sm flex items-center gap-2"
            >
              <i className="fa-regular fa-file-pdf" aria-hidden="true" />
              <span>Download formularen</span>
            </a>
            <Link
              to="/forspoergsel"
              className="bg-[#C68D94] hover:bg-[#A35D67] text-white px-7 py-3.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors shadow-sm"
            >
              Udfyld kageforespørgsel online
            </Link>
          </div>
        </div>
      </section>

      {/* Product Details Modal */}
      {activeProduct && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
          <div
            className="bg-white rounded-3xl max-w-xl w-full max-h-[90vh] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-6 sm:p-8 relative shadow-2xl border border-[#C68D94]/20"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveProduct(null)}
              className="absolute top-4 right-4 max-[666px]:top-2 max-[666px]:right-2 w-9 h-9 text-[#3E3435] hover:text-[#A35D67] flex items-center justify-center transition-colors"
              aria-label="Luk vindue"
            >
              <i className="fa-solid fa-xmark text-lg max-[666px]:relative max-[666px]:-top-1 max-[666px]:left-1" aria-hidden="true" />
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
              <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100">
                {activeProduct.images?.[0] ? (
                  <img
                    src={imageUrl(activeProduct.images[0], 'width=600,height=600,fit=cover,quality=85,format=auto')}
                    alt={productField(activeProduct, 'title', lang)}
                                         className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300">
                    <i className="fa-regular fa-image text-4xl" />
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-lg text-[#3E3435] mb-1">
                                   {productField(activeProduct, 'title', lang)}
                                 </h3>
                <p className="font-bold text-lg text-[#A35D67] mb-3">
                  {activeProduct.price_display || `${activeProduct.price},00 kr.`}
                </p>
                {activeProduct.weight && (
                  <p className="text-xs text-stone-500 mb-2">
                    Vægt: {activeProduct.weight}
                  </p>
                )}
                <p className="text-xs text-[#3E3435]/80 font-light leading-relaxed">
                                   {productField(activeProduct, 'description', lang)}
                                 </p>

                <div className="mt-6 pt-4 border-t border-stone-100 space-y-2">
                  <p className="text-xs font-medium text-[#3E3435]">
                    Bestil eller forhør om lager:
                  </p>
                  <div className="flex gap-2">
                    <a
                      href="tel:60224704"
                      className="flex-1 text-center bg-[#C68D94] hover:bg-[#A35D67] text-white py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
                    >
                      Ring
                    </a>
                    <a
                      href="mailto:karina@fanoechokolade.dk"
                      className="flex-1 text-center border border-[#C68D94]/40 hover:bg-[#F7ECEE] text-[#3E3435] py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
                    >
                      Email
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  ), lang)
}
